// @mui
import { Box, Card, Divider, Container, Button } from '@mui/material'
import { styled } from '@mui/material/styles'
import uniqBy from 'lodash/uniqBy'
// routes
import { PATH_PAGE } from '@/routes/paths'
// hooks
import useSettings from '@/hooks/useSettings'
// layouts
import Layout from '@/layouts/index'
// @types
import { Post } from '@/types/post'
// components
import Page from '@/components/Page'
import { SkeletonPost } from '@/components/skeleton'
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs'
// sections
import { BlogPostHero, BlogPostTags, BlogPostRecent, BlogPostBody } from '@/sections/blog'
import { postService } from '@/services/post'
import { settingService, GlobalSetting, SeoMetadata } from '@/services/page'
import pathService from '@/services/path'
import { notEmpty } from '@/utils/common'
import useApp from '@/hooks/useApp'
import flatten from 'lodash/flatten'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { APPS, APP_MODULES, APP_LOCALES, ModuleEnum } from '@/appConfig'
import type { ParsedUrlQuery } from 'querystring'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { useEffect } from 'react'

interface PathProps extends ParsedUrlQuery {
  appId: string
  title: string
}

interface PostPageProps {
  post: Post | null
  morePosts: Post[]
  globalSetting?: GlobalSetting
  seoMetadataItemsCollection?: SeoMetadata[]
  callToActionLink: {
    title: string
    link: string
  } | null
}
// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(8),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(11),
  },
}))

// ----------------------------------------------------------------------

BlogPost.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

// ----------------------------------------------------------------------

export default function BlogPost({
  post = null,
  morePosts = [],
  globalSetting,
  seoMetadataItemsCollection = [],
  callToActionLink = null,
}: PostPageProps) {
  const { logo, title: rootTitle, host } = useApp()
  const { locale, query, replace } = useRouter()
  const { themeStretch } = useSettings()
  const { title: paramTitle } = query

  useEffect(() => {
    if (post?.title && post.title !== paramTitle) {
      replace(
        {
          pathname: `/post/${post.title}`,
        },
        undefined,
        { shallow: true }
      )
    }
  }, [post])

  return (
    <Page
      title={post?.title ?? ''}
      description={
        post?.content?.json ? documentToPlainTextString(post.content?.json).slice(250) : undefined
      }
      metadataItems={seoMetadataItemsCollection}
      openGraphImage={{ url: post?.previewUrl ?? '', alt: post?.title ?? '' }}
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post?.title,
        inLanguage: locale,
        url: `https://${host}/${locale}/post/${post?.title}`,
        '@id': `https://${host}/${locale}/post/${post?.title}`,
        image: (post?.previewUrl || post?.coverUrl || logo) ?? '',
        datePublished: post?.publishedAt ?? '',
        description: post?.content?.json
          ? documentToPlainTextString(post.content?.json).slice(250)
          : undefined,
        author: {
          '@type': 'Person',
          name: post?.author?.name,
        },
        publisher: {
          '@type': 'Organization',
          name: rootTitle ?? '',
          logo: {
            '@type': 'ImageObject',
            url: logo ?? '',
          },
        },
      }}
    >
      <RootStyle>
        <Container maxWidth={themeStretch ? false : 'lg'} sx={{ pb: 2 }}>
          <HeaderBreadcrumbs
            links={[
              { name: 'Blogs', href: PATH_PAGE.blog },
              post?.categories[0]
                ? {
                    name: post?.categories[0] ?? '',
                    href: `${PATH_PAGE.blogCategory}/${post?.categories[0]}`,
                  }
                : undefined,
              { name: post?.title ?? '' },
            ].filter(notEmpty)}
          />
          {post && (
            <Card>
              <BlogPostHero post={post} />
              {Boolean(post.tags.length) && (
                <Box sx={{ px: { xs: 3, md: 5 } }}>
                  <BlogPostTags post={post} />
                </Box>
              )}
              <Divider />
              <Box sx={{ p: { xs: 3, md: 5 } }}>
                <BlogPostBody content={post.content} />
              </Box>
              {(callToActionLink || globalSetting?.callToActionLink) && (
                <>
                  <Divider />
                  <Box sx={{ p: { xs: 3, md: 5 } }}>
                    <Button
                      size="large"
                      variant="contained"
                      target="_blank"
                      rel="noopener"
                      href={callToActionLink?.link ?? globalSetting?.callToActionLink?.link ?? ''}
                    >
                      {callToActionLink?.title ?? globalSetting?.callToActionLink?.title}
                    </Button>
                  </Box>
                </>
              )}
            </Card>
          )}

          {!post && <SkeletonPost />}

          {Boolean(morePosts.length) && <BlogPostRecent posts={morePosts} />}
        </Container>
      </RootStyle>
    </Page>
  )
}

export const getStaticProps: GetStaticProps<PostPageProps, PathProps> = async ({
  params,
  locales = [],
  locale,
}) => {
  if (!params) throw new Error('No path parameters found')
  const { appId } = params
  const { getPostAndMorePostsById, getPostIdByPostTitle } = postService(appId)
  const { getGlobalSetting } = settingService(appId)
  const globalSetting = await getGlobalSetting({ locale })

  let postId: string | null = null
  let post: Post | null = null
  let morePosts: Post[] = []
  let seoMetadataItemsCollection: SeoMetadata[] = []
  let callToActionLink = null

  postId = await getPostIdByPostTitle(params.title, { locale })
  if (
    !postId &&
    APP_MODULES.find(
      (appModule) => appModule.appId === appId && appModule.module === ModuleEnum.Locale
    )
  ) {
    const appLocales = APP_LOCALES.filter((appLocale) => appLocale.appId === appId).map(
      (appLocale) => String(appLocale.value)
    )
    const filteredLocales = locales.filter((l) => appLocales.includes(l) && l !== locale)
    for await (const filteredLocale of filteredLocales) {
      const localedPostId = await getPostIdByPostTitle(params.title, { locale: filteredLocale })
      if (localedPostId) {
        postId = localedPostId
        break
      }
    }
  }

  if (postId) {
    const data = await getPostAndMorePostsById(postId, true, { locale })
    post = data.post
    morePosts = data.morePosts
    seoMetadataItemsCollection = data.seoMetadataItemsCollection
    callToActionLink = data.callToActionLink ?? null
  }

  return {
    props: {
      post,
      morePosts,
      seoMetadataItemsCollection,
      globalSetting,
      callToActionLink,
    },
    revalidate: 3600,
  }
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getPostPaths } = pathService()

  const allAppPostPaths = await Promise.allSettled(
    APPS.map(async (app) => getPostPaths(app.id))
  ).then((res) => res.map((r) => (r.status === 'fulfilled' ? r.value : null)).filter(notEmpty))

  // test removing duplicates
  const allPostPaths = uniqBy(
    flatten(allAppPostPaths),
    (item) => item.params.title.toLocaleLowerCase() + item.params.appId
  )

  return {
    paths: allPostPaths,
    fallback: true,
  }
}
